/** Stock Balance — one screen where GHERP had seven reports.
 *
 * `frmRptStockBal`, `frmRptREJStockBal` and `frmRptWIPStockBal` — 3,068 lines
 * between them — read the SAME table and differed by a filter. Add
 * `VendStockBal`, `StockLevelReport`, `SurplusStock` and
 * `SurplusStockPurchase` and it is seven screens for one question.
 * `SCREEN_REGISTER.md` §2, ledger D28.
 *
 * Here the pool is a COLUMN. Stock is keyed on item + location + stage, so
 * "rejected", "work in progress" and "at the subcontractor" are a stage and a
 * location rather than three more screens. SAP does the same with one
 * transaction, `MMBE`, and a stock-type column.
 *
 * NOTHING ON THIS SCREEN IS STORED. Every figure is derived from
 * `erp_stock_movement` when you ask for it. GHERP's `rptStkBal` was a stored
 * total written from 136 separate places and read from 184, and once a stored
 * total drifts from the ledger nothing can say which of the two is right.
 * That is the defect this whole rebuild exists to correct, and this is the
 * screen where you can see the correction working.
 */

import { useCallback, useEffect, useState } from 'react'
import { api, ApiError } from '@/lib/api'
import { SearchSelect } from '@/components/SearchSelect'

interface Row {
  item_id: string
  item_name: string
  uom: string
  location_id: string
  location_name: string
  stage: string
  stage_name: string | null
  qty: number
  avg_rate?: number
  value?: number
}
interface Ref { code: string; name: string }

const num = (v: unknown, dp = 3) =>
  v === null || v === undefined || v === ''
    ? '—'
    : Number(v).toLocaleString('en-IN',
        { minimumFractionDigits: dp, maximumFractionDigits: dp })

export function StockBalanceScreen() {
  const [rows, setRows] = useState<Row[]>([])
  const [locations, setLocations] = useState<Ref[]>([])
  const [stages, setStages] = useState<Ref[]>([])
  const [groups, setGroups] = useState<Ref[]>([])
  const [f, setF] = useState({ location_id: '', stage: '', group_code: '', q: '' })
  const [valuesVisible, setValuesVisible] = useState(true)
  const [loading, setLoading] = useState(true)
  const [problem, setProblem] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await api.get<{ rows: Row[]; values_visible: boolean }>(
        '/stores/stock', {
          location_id: f.location_id || undefined,
          stage: f.stage || undefined,
          group_code: f.group_code || undefined,
          q: f.q || undefined,
        })
      setRows(r.rows ?? [])
      setValuesVisible(r.values_visible)
      setProblem(null)
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message
                                       : 'The balance could not be read.')
    } finally {
      setLoading(false)
    }
  }, [f])

  useEffect(() => { void load() }, [load])

  useEffect(() => {
    Promise.all([
      api.get<{ rows: { location_id: string; name: string }[] }>('/masters/locations'),
      api.get<{ rows: { stage_code: string; name: string }[] }>('/masters/stages'),
      api.get<{ rows: { group_code: string; name: string }[] }>('/masters/item-groups'),
    ]).then(([l, s, g]) => {
      setLocations((l.rows ?? []).map(r => ({ code: r.location_id, name: r.name })))
      setStages((s.rows ?? []).map(r => ({ code: r.stage_code, name: r.name })))
      setGroups((g.rows ?? []).map(r => ({ code: r.group_code, name: r.name })))
    }).catch(() => { /* filters degrade to "all"; the list still works */ })
  }, [])

  const totalValue = rows.reduce((t, r) => t + Number(r.value ?? 0), 0)

  return (
    <div className="screen">
      <div className="page-head">
        <div>
          <h1 className="page-title">Stock Balance</h1>
          <p className="page-sub">
            {loading ? 'Reading the ledger…'
                     : `${rows.length} position${rows.length === 1 ? '' : 's'}`}
            {valuesVisible && rows.length > 0
              && ` · ₹${num(totalValue, 2)} total`}
          </p>
        </div>
      </div>

      {problem && <div className="form-errors">{problem}</div>}

      <div className="card">
        <div className="card-head toolbar">
          <input
            className="search"
            placeholder="Search by item code or name…"
            value={f.q}
            onChange={e => setF({ ...f, q: e.target.value })}
          />
          <div style={{ minWidth: 190 }}>
            <SearchSelect
              value={f.location_id}
              emptyLabel="All locations"
              onChange={v => setF({ ...f, location_id: v })}
              options={locations.map(l => ({ value: l.code, label: l.name }))}
            />
          </div>
          <div style={{ minWidth: 190 }}>
            <SearchSelect
              value={f.stage}
              emptyLabel="All stages"
              onChange={v => setF({ ...f, stage: v })}
              options={stages.map(s => ({ value: s.code, label: s.name }))}
            />
          </div>
          <div style={{ minWidth: 190 }}>
            <SearchSelect
              value={f.group_code}
              emptyLabel="All groups"
              onChange={v => setF({ ...f, group_code: v })}
              options={groups.map(g => ({ value: g.code, label: g.name }))}
            />
          </div>
        </div>

        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Item</th><th>Location</th><th>Stage</th>
                <th className="num">Quantity</th><th>UOM</th>
                {valuesVisible && <th className="num">Avg Rate</th>}
                {valuesVisible && <th className="num">Value</th>}
              </tr>
            </thead>
            <tbody>
              {!loading && rows.length === 0 && (
                <tr><td colSpan={valuesVisible ? 7 : 5} className="empty-cell">
                  {f.q || f.location_id || f.stage || f.group_code
                    ? 'Nothing matches those filters.'
                    : 'No stock yet. A balance appears here as soon as a goods '
                      + 'receipt is authorised — this screen adds nothing up, '
                      + 'it reads the movement ledger.'}
                </td></tr>
              )}
              {rows.map(r => (
                <tr key={`${r.item_id}|${r.location_id}|${r.stage}`}>
                  <td>{r.item_id} — {r.item_name}</td>
                  <td>{r.location_name}</td>
                  <td>
                    {/* A stage that cannot be issued is worth seeing at a
                        glance, not discovering when an issue is refused. */}
                    <span className={'badge badge-'
                      + (r.stage === 'INSPECTION' ? 'submitted'
                        : r.stage === 'REJECTED' ? 'cancelled'
                        : r.stage === 'STOCK' ? 'active' : 'draft')}>
                      {r.stage_name ?? r.stage}
                    </span>
                  </td>
                  <td className="num">{num(r.qty)}</td>
                  <td>{r.uom}</td>
                  {valuesVisible && <td className="num">{num(r.avg_rate, 2)}</td>}
                  {valuesVisible && <td className="num">{num(r.value, 2)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!valuesVisible && rows.length > 0 && (
          <p className="form-note">
            Rates and values are hidden for your account.
          </p>
        )}
      </div>
    </div>
  )
}
